
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export function PasswordForm() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handlePasswordChange = async () => {
    // Reset error state
    setError(null);
    
    // Validate passwords
    if (!currentPassword) {
      setError("Digite sua senha atual.");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("As novas senhas não coincidem.");
      return;
    }
    
    if (newPassword.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // First verify the current password is correct
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || '',
        password: currentPassword,
      });
      
      if (signInError) {
        throw new Error("Senha atual incorreta.");
      }
      
      // Now update the password
      await updatePassword(newPassword);
      
      toast({
        title: "Senha Atualizada",
        description: "Sua senha foi alterada com sucesso.",
      });
      
      // Clear the form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Erro ao atualizar senha:", error);
      
      setError(error.message || "Ocorreu um erro ao atualizar sua senha. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Senha</CardTitle>
        <CardDescription>
          Altere sua senha
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <Label className="text-sm font-medium" htmlFor="currentPassword">
            Senha Atual
          </Label>
          <Input 
            id="currentPassword"
            type="password" 
            value={currentPassword} 
            onChange={(e) => setCurrentPassword(e.target.value)} 
            disabled={isSubmitting}
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm font-medium" htmlFor="newPassword">
            Nova Senha
          </Label>
          <Input 
            id="newPassword"
            type="password" 
            value={newPassword} 
            onChange={(e) => setNewPassword(e.target.value)} 
            disabled={isSubmitting}
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm font-medium" htmlFor="confirmPassword">
            Confirme a Nova Senha
          </Label>
          <Input 
            id="confirmPassword"
            type="password" 
            value={confirmPassword} 
            onChange={(e) => setConfirmPassword(e.target.value)} 
            disabled={isSubmitting}
          />
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handlePasswordChange} 
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Atualizando...
            </>
          ) : (
            "Atualizar Senha"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
